import { T } from 'consts/translationKeys'
import { useDropdownState } from 'hooks/useDropdownState'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { translate } from 'utils/translations'
import * as Styled from './styled'
import Text from 'common-components/Text'
import TextWithIconTooltip from 'common-components/TextWithIconTooltip'
import TtsFlyout from '../Flyout'
import VoiceToneContainer from '../SpeakerTone'

interface IBulkToneProps {
  hasSpeakerEmotion: boolean
  initialToneInstruction?: string
  onToneInstructionChange: (instruction?: string) => void
}

interface IToneInstructionContentProps {
  toneInstruction?: string
  setToneInstruction: (instruction: string) => void
}

const ToneInstructionContent = ({
  toneInstruction,
  setToneInstruction
}: IToneInstructionContentProps) => (
  <Styled.BulkToneContainer>
    <TextWithIconTooltip
      className="icon-with-tooltip"
      title={translate(T.SET_VOICE_TONE)}
      tooltipText={translate(T.SET_VOICE_TONE_TOOLTIP)}
      gap={4}
      textProps={{
        type: 'subtitle',
        color: 'secondarySemiDark',
        fontWeight: '700'
      }}
      iconProps={{ color: 'secondarySemiLight', customSize: 20 }}
      tooltipPosition="top"
      showMobileTooltip
    />
    <VoiceToneContainer
      toneInstruction={toneInstruction}
      setToneInstruction={setToneInstruction}
    />
  </Styled.BulkToneContainer>
)

const BulkTone = ({
  hasSpeakerEmotion,
  initialToneInstruction,
  onToneInstructionChange
}: IBulkToneProps) => {
  const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(
    null
  )
  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState()
  const [toneInstruction, setToneInstruction] = useState(initialToneInstruction)

  const handleConfirmButton = useCallback(() => {
    closeDropdown()
    onToneInstructionChange(toneInstruction)
  }, [closeDropdown, onToneInstructionChange, toneInstruction])

  const handleCloseButton = useCallback(() => {
    closeDropdown()
    setToneInstruction(initialToneInstruction)
  }, [closeDropdown, initialToneInstruction, setToneInstruction])

  const memoizedToneInstructionContent = useMemo(
    () => (
      <ToneInstructionContent
        toneInstruction={toneInstruction}
        setToneInstruction={setToneInstruction}
      />
    ),
    [toneInstruction, setToneInstruction]
  )

  const displayText = useMemo(
    () =>
      hasSpeakerEmotion &&
      (initialToneInstruction || translate(T.SET_VOICE_TONE)),
    [hasSpeakerEmotion, initialToneInstruction]
  )

  const haveToneInstructionChanged = useMemo(
    () => initialToneInstruction !== toneInstruction,
    [initialToneInstruction, toneInstruction]
  )

  useEffect(() => {
    setToneInstruction(initialToneInstruction)
  }, [initialToneInstruction])

  return (
    <>
      <Styled.BulkToneToggleContainer
        ref={setWrapperElement}
        $isVisible={hasSpeakerEmotion}
        onClick={toggleDropdown}
      >
        <Text
          type="h2"
          color="primaryDark"
          lineHeight="17px"
          ellipsis
          className="bulk-tone-text"
        >
          {displayText}
        </Text>
      </Styled.BulkToneToggleContainer>
      {isOpen && (
        <TtsFlyout
          isOpen={isOpen}
          togglerElement={wrapperElement}
          onClose={handleCloseButton}
          hasAlertTooltip={haveToneInstructionChanged}
          alertTooltipText={translate(T.SAVE_CHANGES_ALERT_TOOLTIP)}
          buttonsConfig={{
            confirmButton: {
              title: translate(T.SAVE),
              onClick: handleConfirmButton
            },
            cancelButton: {
              title: translate(T.CANCEL),
              onClick: handleCloseButton
            }
          }}
        >
          {memoizedToneInstructionContent}
        </TtsFlyout>
      )}
    </>
  )
}

export default memo(BulkTone)
